import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { useApi, useTenantContext } from '../lib/api';

type MetricsPeriod = 'week' | 'month';

type AiQualityBucket = {
  total: number;
  low_confidence: number;
  fallback: number;
  failures: number;
  average_confidence: number | null;
  low_confidence_rate: number;
  fallback_rate: number;
  failure_rate: number;
};

type IntentConversionMetrics = {
  total: number;
  converted: number;
  conversion_rate: number;
  outcomes: Record<string, number>;
};

type IntentHandoffMetrics = {
  total: number;
  handoff: number;
  handoff_rate: number;
  average_time_to_handoff_seconds: number | null;
};

type IntentAbandonmentMetrics = {
  total: number;
  abandoned: number;
  abandonment_rate: number;
  lost_opportunities: number;
  lost_opportunity_rate: number;
};

type IntentFunnelMetrics = {
  total_cases: number;
  waiting_customer_info: number;
  handoff: number;
  human_replied: number;
  converted: number;
  abandoned: number;
  lost_opportunities: number;
  open: number;
  closed: number;
};

type IntentAnalyticsRow = {
  intent: string;
  total: number;
  converted: number;
  conversion_rate: number;
  abandoned: number;
  abandonment_rate: number;
  handoff: number;
  handoff_rate: number;
  lost_opportunities: number;
  lost_opportunity_rate: number;
  open: number;
  closed: number;
};

type LostOpportunityCase = {
  case_id: string;
  intent: string;
  status: string;
  outcome: string | null;
  customer_name: string | null;
  updated_at: string;
  reason: string;
};

type LostOpportunitiesMetrics = {
  total: number;
  by_intent: Record<string, number>;
  recent_cases: LostOpportunityCase[];
};

type AiQualityBreakdownEntry = AiQualityBucket & {
  provider: string;
  model: string;
  vertical: string;
  intent: string;
};

type AiQualityIncident = {
  log_id: string;
  created_at: string;
  case_id: string | null;
  provider: string;
  model: string;
  vertical: string;
  intent: string;
  confidence: number | null;
  threshold: number | null;
  low_confidence: boolean;
  fallback_used: boolean;
  failed: boolean;
  policy_action: string | null;
  policy_reason: string | null;
  fallback_reason: string | null;
  failure_reason: string | null;
};

type AiQualityMetrics = {
  total_evaluations: number;
  low_confidence: number;
  fallback: number;
  failures: number;
  average_confidence: number | null;
  low_confidence_rate: number;
  fallback_rate: number;
  failure_rate: number;
  by_provider: Record<string, AiQualityBucket>;
  by_model: Record<string, AiQualityBucket>;
  by_vertical: Record<string, AiQualityBucket>;
  by_intent: Record<string, AiQualityBucket>;
  breakdown: AiQualityBreakdownEntry[];
  recent_events: AiQualityIncident[];
  report: { format: 'json'; dimensions: string[]; rows: number; generated_at: string };
};

type BillingMetricUsage = {
  metric: string;
  used: number;
  limit: number | null;
  remaining: number | null;
  ratio: number | null;
  status: 'ok' | 'warn' | 'exceeded';
  action: 'none' | 'alert' | 'soft_block';
};

type BillingEvaluation = {
  tenant_id: string;
  generated_at: string;
  period: 'month';
  from: string;
  to: string;
  plan: {
    enabled: boolean;
    plan_id: string;
    plan_name: string;
    period: 'month';
    currency: 'BRL';
    price_brl_monthly?: number | null;
    enforcement: 'alert' | 'soft_block';
    warn_at_ratio: number;
    limits: Record<string, number | null | undefined>;
  };
  totals: Record<string, number>;
  status: 'ok' | 'warn' | 'exceeded';
  action: 'none' | 'alert' | 'soft_block';
  metrics: BillingMetricUsage[];
  alerts: string[];
};

type BenchmarkMetric = {
  tenant_value: number | null;
  benchmark_value: number;
  delta: number | null;
  unit: 'seconds' | 'rate';
  direction: 'lower_is_better' | 'higher_is_better';
};

type BenchmarkResponse = {
  vertical: string;
  period: MetricsPeriod;
  from: string;
  to: string;
  unit_id: string | null;
  generated_at: string;
  privacy: {
    anonymized: boolean;
    aggregation: string;
    min_tenants: number;
    min_cases: number;
    excludes_tenant_identifiers: boolean;
    lgpd_note: string;
  };
  sample: {
    benchmark_cases: number;
    benchmark_tenants: number;
    tenant_cases: number;
    tenant_opportunities: number;
  };
  metrics: {
    average_first_response_seconds: BenchmarkMetric;
    handoff_rate: BenchmarkMetric;
    conversion_rate: BenchmarkMetric;
    abandonment_rate: BenchmarkMetric;
  };
  recommendations: string[];
};

type MeteringUsageResponse = {
  from: string;
  to: string;
  tenant_id: string | null;
  generated_at: string;
  totals: Record<string, number>;
  billing?: BillingEvaluation;
  billing_error?: { error: string; message?: string };
};

type ExecutiveReportMetric = {
  key: string;
  label: string;
  value: number | null;
  formatted_value: string;
  description: string;
  tone: 'neutral' | 'good' | 'warn' | 'alert';
};

type ExecutiveReport = {
  title: string;
  period_label: string;
  unit_id: string | null;
  generated_at: string;
  summary: {
    leads: number;
    appointments: number;
    quotes: number;
    handoffs: number;
    lost_opportunities: number;
    average_first_response_seconds: number | null;
    conversion_rate: number;
  };
  cards: ExecutiveReportMetric[];
  funnel: Array<{ stage: string; value: number; description: string }>;
  insights: string[];
  by_unit: Array<{ unit_id: string; total: number; share: number }>;
};

type OperationalMetrics = {
  period: MetricsPeriod;
  from: string;
  to: string;
  generated_at: string;
  tenant_id: string;
  unit_id: string | null;
  sla_minutes: number;
  totals: { cases: number; open: number };
  executive_report: ExecutiveReport;
  product: {
    open_cases: number;
    awaiting_human_cases: number;
    in_progress_cases: number;
    closed_cases: number;
    closed_cases_in_period: number;
    average_first_response_seconds: number | null;
    average_time_to_handoff_seconds: number | null;
    handoff_rate: number;
    cases_outside_sla: number;
    volume_by_intent: Record<string, number>;
    volume_by_unit: Record<string, number>;
    conversion_by_intent: Record<string, IntentConversionMetrics>;
    handoff_by_intent: Record<string, IntentHandoffMetrics>;
    abandonment_by_intent: Record<string, IntentAbandonmentMetrics>;
    case_funnel_by_intent: Record<string, IntentFunnelMetrics>;
    intent_analytics: IntentAnalyticsRow[];
    lost_opportunities: LostOpportunitiesMetrics;
    by_status: Record<string, number>;
    by_priority: Record<string, number>;
    by_outcome: Record<string, number>;
    queues: Record<string, number>;
    status_counters: Record<string, number>;
  };
  technical: {
    webhook_errors: number;
    whatsapp_send_failures: number;
    duplicate_messages: number;
    dlq_events: number;
    average_pipeline_latency_ms: number | null;
    ai_failures: number;
    fallback_by_provider: Record<string, number>;
    ai_quality: AiQualityMetrics;
  };
};

function meteringRange(period: MetricsPeriod) {
  const toDate = new Date();
  const fromDate = new Date(toDate);
  fromDate.setUTCDate(toDate.getUTCDate() - (period === 'week' ? 6 : 29));
  return { from: fromDate.toISOString().slice(0, 10), to: toDate.toISOString().slice(0, 10) };
}

function formatCurrency(value: number | null | undefined) {
  if (value === null || value === undefined) return 'Sob consulta';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

function formatBillingRatio(value: number | null) {
  if (value === null) return '-';
  return formatPercent(value);
}

function billingTone(status: BillingEvaluation['status']) {
  if (status === 'exceeded') return 'alert' as const;
  if (status === 'warn') return 'warn' as const;
  return undefined;
}

function billingStatusLabel(
  status: BillingEvaluation['status'],
  action: BillingEvaluation['action'],
) {
  if (action === 'soft_block') return 'Bloqueio suave';
  if (status === 'exceeded') return 'Limite excedido';
  if (status === 'warn') return 'Alerta de uso';
  return 'Dentro do plano';
}

function billingMetricLabel(metric: string) {
  const labels: Record<string, string> = {
    messages_received: 'Mensagens recebidas',
    messages_sent: 'Mensagens enviadas',
    cases_created: 'Casos criados',
    cases_closed: 'Casos encerrados',
    ai_calls: 'Chamadas de IA',
    flows_used: 'Flows usados',
    active_users: 'Usuarios ativos',
    active_tenants: 'Tenants ativos',
    integrations_triggered: 'Integracoes acionadas',
  };
  return labels[metric] ?? metric;
}
function formatNumber(value: number | null | undefined) {
  if (value === null || value === undefined) return '-';
  return new Intl.NumberFormat('pt-BR').format(value);
}

function formatPercent(value: number) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(value);
}

function formatOptionalPercent(value: number | null | undefined) {
  if (value === null || value === undefined) return '-';
  return formatPercent(value);
}

function formatDurationSeconds(value: number | null) {
  if (value === null) return '-';
  if (value < 60) return `${value}s`;
  const minutes = Math.floor(value / 60);
  const seconds = value % 60;
  if (minutes < 60) return seconds ? `${minutes}m ${seconds}s` : `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const restMinutes = minutes % 60;
  return restMinutes ? `${hours}h ${restMinutes}m` : `${hours}h`;
}

function formatMs(value: number | null) {
  if (value === null) return '-';
  if (value < 1000) return `${value}ms`;
  return `${(value / 1000).toFixed(1)}s`;
}

function sortedEntries(input: Record<string, number>) {
  return Object.entries(input).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
}

function sortedBucketEntries(input: Record<string, AiQualityBucket>) {
  return Object.entries(input).sort((a, b) => b[1].total - a[1].total || a[0].localeCompare(b[0]));
}

function sortedConversionEntries(input: Record<string, IntentConversionMetrics>) {
  return Object.entries(input).sort((a, b) => b[1].total - a[1].total || a[0].localeCompare(b[0]));
}

function sortedFunnelEntries(input: Record<string, IntentFunnelMetrics>) {
  return Object.entries(input).sort(
    (a, b) => b[1].total_cases - a[1].total_cases || a[0].localeCompare(b[0]),
  );
}

function csvCell(value: unknown) {
  const raw = value === null || value === undefined ? '' : String(value);
  return `"${raw.replace(/"/g, '""')}"`;
}

function aiBreakdownCsv(metrics: OperationalMetrics) {
  const header = [
    'provider',
    'model',
    'vertical',
    'intent',
    'total',
    'low_confidence',
    'fallback',
    'failures',
    'average_confidence',
    'low_confidence_rate',
    'fallback_rate',
    'failure_rate',
  ];
  const rows = metrics.technical.ai_quality.breakdown.map((row) => [
    row.provider,
    row.model,
    row.vertical,
    row.intent,
    row.total,
    row.low_confidence,
    row.fallback,
    row.failures,
    row.average_confidence ?? '',
    row.low_confidence_rate,
    row.fallback_rate,
    row.failure_rate,
  ]);
  return [header, ...rows].map((row) => row.map(csvCell).join(',')).join('\n');
}

function downloadAiReport(metrics: OperationalMetrics) {
  const csv = aiBreakdownCsv(metrics);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `ai-quality-${metrics.tenant_id}-${metrics.period}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function MetricCard(props: { label: string; value: string; tone?: 'alert' | 'warn' | undefined }) {
  return (
    <div className={['metric-card', props.tone ? `metric-card-${props.tone}` : ''].join(' ')}>
      <span>{props.label}</span>
      <strong>{props.value}</strong>
    </div>
  );
}

function MetricList(props: { title: string; items: Record<string, number> }) {
  const entries = sortedEntries(props.items);
  return (
    <section className="panel metric-table">
      <h2>{props.title}</h2>
      {entries.length ? (
        <table>
          <tbody>
            {entries.map(([label, value]) => (
              <tr key={label}>
                <th>{label}</th>
                <td>{formatNumber(value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="hint">Sem dados no periodo.</p>
      )}
    </section>
  );
}
function ConversionByIntentTable(props: { items: Record<string, IntentConversionMetrics> }) {
  const entries = sortedConversionEntries(props.items);
  return (
    <section className="panel metric-table metric-detail-table">
      <h2>Conversao por intencao</h2>
      {entries.length ? (
        <table>
          <thead>
            <tr>
              <th>Intencao</th>
              <th>Total</th>
              <th>Convertidos</th>
              <th>Taxa</th>
            </tr>
          </thead>
          <tbody>
            {entries.map(([label, value]) => (
              <tr key={label}>
                <th>{label}</th>
                <td className="numeric">{formatNumber(value.total)}</td>
                <td className="numeric">{formatNumber(value.converted)}</td>
                <td className="numeric">{formatPercent(value.conversion_rate)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="hint">Sem dados no periodo.</p>
      )}
    </section>
  );
}

function IntentAnalyticsTable(props: { rows: IntentAnalyticsRow[] }) {
  return (
    <section className="panel metric-table metric-detail-table metric-table-wide">
      <h2>Resumo por intencao</h2>
      {props.rows.length ? (
        <table>
          <thead>
            <tr>
              <th>Intencao</th>
              <th>Total</th>
              <th>Conversao</th>
              <th>Abandono</th>
              <th>Handoff</th>
              <th>Perdidas</th>
              <th>Abertos</th>
              <th>Encerrados</th>
            </tr>
          </thead>
          <tbody>
            {props.rows.map((row) => (
              <tr key={row.intent}>
                <th>{row.intent}</th>
                <td className="numeric">{formatNumber(row.total)}</td>
                <td className="numeric">
                  {formatNumber(row.converted)} / {formatPercent(row.conversion_rate)}
                </td>
                <td className="numeric">
                  {formatNumber(row.abandoned)} / {formatPercent(row.abandonment_rate)}
                </td>
                <td className="numeric">
                  {formatNumber(row.handoff)} / {formatPercent(row.handoff_rate)}
                </td>
                <td className="numeric">
                  {formatNumber(row.lost_opportunities)} /{' '}
                  {formatPercent(row.lost_opportunity_rate)}
                </td>
                <td className="numeric">{formatNumber(row.open)}</td>
                <td className="numeric">{formatNumber(row.closed)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="hint">Sem dados no periodo.</p>
      )}
    </section>
  );
}

function FunnelByIntentTable(props: { items: Record<string, IntentFunnelMetrics> }) {
  const entries = sortedFunnelEntries(props.items);
  return (
    <section className="panel metric-table metric-detail-table metric-table-wide">
      <h2>Funil por caso</h2>
      {entries.length ? (
        <table>
          <thead>
            <tr>
              <th>Intencao</th>
              <th>Casos</th>
              <th>Aguardando info</th>
              <th>Handoff</th>
              <th>Resposta humana</th>
              <th>Convertidos</th>
              <th>Abandono</th>
              <th>Perdidas</th>
            </tr>
          </thead>
          <tbody>
            {entries.map(([intent, value]) => (
              <tr key={intent}>
                <th>{intent}</th>
                <td className="numeric">{formatNumber(value.total_cases)}</td>
                <td className="numeric">{formatNumber(value.waiting_customer_info)}</td>
                <td className="numeric">{formatNumber(value.handoff)}</td>
                <td className="numeric">{formatNumber(value.human_replied)}</td>
                <td className="numeric">{formatNumber(value.converted)}</td>
                <td className="numeric">{formatNumber(value.abandoned)}</td>
                <td className="numeric">{formatNumber(value.lost_opportunities)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="hint">Sem dados no periodo.</p>
      )}
    </section>
  );
}

function LostOpportunitiesTable(props: { metrics: LostOpportunitiesMetrics }) {
  const byIntent = sortedEntries(props.metrics.by_intent);
  return (
    <section className="panel metric-table metric-detail-table metric-table-wide">
      <h2>Oportunidades perdidas</h2>
      {props.metrics.total ? (
        <>
          {byIntent.length ? (
            <p className="hint">
              {byIntent.map(([intent, value]) => `${intent}: ${formatNumber(value)}`).join(' | ')}
            </p>
          ) : null}
          <table>
            <thead>
              <tr>
                <th>Caso</th>
                <th>Cliente</th>
                <th>Intencao</th>
                <th>Motivo</th>
                <th>Outcome</th>
                <th>Atualizado</th>
              </tr>
            </thead>
            <tbody>
              {props.metrics.recent_cases.map((row) => (
                <tr key={row.case_id}>
                  <th>
                    <Link to={`/cases/${row.case_id}`}>{row.case_id}</Link>
                  </th>
                  <td>{row.customer_name ?? '-'}</td>
                  <td>{row.intent}</td>
                  <td>{row.reason}</td>
                  <td>{row.outcome ?? row.status}</td>
                  <td>{new Date(row.updated_at).toLocaleString('pt-BR')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : (
        <p className="hint">Sem oportunidades perdidas no periodo.</p>
      )}
    </section>
  );
}

function executiveCardTone(tone: ExecutiveReportMetric['tone']) {
  return tone === 'alert' || tone === 'warn' ? tone : undefined;
}

function ExecutiveReportPanel(props: { report: ExecutiveReport }) {
  const unitLabel = props.report.unit_id ? `Unidade ${props.report.unit_id}` : 'Todas as unidades';
  const units = props.report.by_unit.filter((row) => row.unit_id !== 'unknown');

  return (
    <section className="executive-report-section">
      <div className="section-header">
        <div>
          <h2>{props.report.title}</h2>
          <p className="hint">
            {props.report.period_label} | {unitLabel}
          </p>
        </div>
      </div>

      <section className="metric-grid">
        {props.report.cards.map((card) => (
          <MetricCard
            key={card.key}
            label={card.label}
            value={card.formatted_value}
            tone={executiveCardTone(card.tone)}
          />
        ))}
      </section>

      <div className="metric-columns">
        <section className="panel metric-table metric-detail-table">
          <h2>Funil executivo</h2>
          {props.report.funnel.length ? (
            <table>
              <tbody>
                {props.report.funnel.map((stage) => (
                  <tr key={stage.stage}>
                    <th>{stage.stage}</th>
                    <td className="numeric">{formatNumber(stage.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="hint">Sem dados no periodo.</p>
          )}
        </section>

        <section className="panel metric-table metric-detail-table">
          <h2>Leitura gerencial</h2>
          {props.report.insights.length ? (
            <table>
              <tbody>
                {props.report.insights.map((insight) => (
                  <tr key={insight}>
                    <td>{insight}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="hint">Sem insights para o periodo.</p>
          )}
        </section>

        {units.length > 1 ? (
          <section className="panel metric-table metric-detail-table">
            <h2>Volume por unidade</h2>
            <table>
              <tbody>
                {units.map((row) => (
                  <tr key={row.unit_id}>
                    <th>{row.unit_id}</th>
                    <td className="numeric">
                      {formatNumber(row.total)} / {formatPercent(row.share)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        ) : null}
      </div>
    </section>
  );
}

function BenchmarkValue(props: { metric: BenchmarkMetric }) {
  const tenant =
    props.metric.unit === 'seconds'
      ? formatDurationSeconds(props.metric.tenant_value)
      : formatOptionalPercent(props.metric.tenant_value);
  const benchmark =
    props.metric.unit === 'seconds'
      ? formatDurationSeconds(props.metric.benchmark_value)
      : formatPercent(props.metric.benchmark_value);
  return (
    <td className="numeric">
      {tenant} / {benchmark}
    </td>
  );
}

function BenchmarksPanel(props: { benchmarks: BenchmarkResponse | null }) {
  const benchmarks = props.benchmarks;
  if (!benchmarks) return null;

  return (
    <section className="benchmarks-section">
      <div className="section-header">
        <div>
          <h2>Benchmarks por vertical</h2>
          <p className="hint">
            {benchmarks.vertical} | {formatNumber(benchmarks.sample.benchmark_tenants)} tenants |{' '}
            {formatNumber(benchmarks.sample.benchmark_cases)} casos agregados
          </p>
        </div>
      </div>

      <section className="panel metric-table metric-detail-table metric-table-wide">
        <h2>Comparativo</h2>
        <table>
          <thead>
            <tr>
              <th>Metrica</th>
              <th>Tenant / mercado</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th>Tempo de resposta</th>
              <BenchmarkValue metric={benchmarks.metrics.average_first_response_seconds} />
            </tr>
            <tr>
              <th>Handoff</th>
              <BenchmarkValue metric={benchmarks.metrics.handoff_rate} />
            </tr>
            <tr>
              <th>Conversao</th>
              <BenchmarkValue metric={benchmarks.metrics.conversion_rate} />
            </tr>
            <tr>
              <th>Abandono</th>
              <BenchmarkValue metric={benchmarks.metrics.abandonment_rate} />
            </tr>
          </tbody>
        </table>
        <p className="hint">{benchmarks.privacy.lgpd_note}</p>
      </section>

      <section className="panel metric-table metric-detail-table metric-table-wide">
        <h2>Recomendacoes</h2>
        <table>
          <tbody>
            {benchmarks.recommendations.map((item) => (
              <tr key={item}>
                <td>{item}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </section>
  );
}

function BillingPanel(props: { usage: MeteringUsageResponse | null }) {
  const billing = props.usage?.billing;
  if (!props.usage) return null;

  if (!billing) {
    return (
      <section className="panel metric-table metric-detail-table metric-table-wide">
        <h2>Billing</h2>
        <p className="hint">
          Billing indisponivel para este periodo
          {props.usage.billing_error?.message ? `: ${props.usage.billing_error.message}` : '.'}
        </p>
      </section>
    );
  }

  const limitedMetrics = billing.metrics.filter((metric) => metric.limit !== null);

  return (
    <section className="billing-section">
      <div className="section-header">
        <div>
          <h2>Billing</h2>
          <p className="hint">
            Plano {billing.plan.plan_name} | {billing.from} ate {billing.to}
          </p>
        </div>
      </div>

      <section className="metric-grid metric-grid-technical">
        <MetricCard label="Plano" value={billing.plan.plan_name} />
        <MetricCard label="Mensalidade" value={formatCurrency(billing.plan.price_brl_monthly)} />
        <MetricCard
          label="Status"
          value={billingStatusLabel(billing.status, billing.action)}
          tone={billingTone(billing.status)}
        />
        <MetricCard
          label="Politica"
          value={billing.plan.enforcement === 'soft_block' ? 'Bloqueio suave' : 'Alerta'}
          tone={billing.action === 'soft_block' ? 'alert' : undefined}
        />
      </section>

      <section className="panel metric-table metric-detail-table metric-table-wide">
        <h2>Uso por limite</h2>
        {limitedMetrics.length ? (
          <table>
            <thead>
              <tr>
                <th>Metrica</th>
                <th>Uso</th>
                <th>Limite</th>
                <th>Restante</th>
                <th>Consumo</th>
                <th>Acao</th>
              </tr>
            </thead>
            <tbody>
              {limitedMetrics.map((metric) => (
                <tr key={metric.metric}>
                  <th>{billingMetricLabel(metric.metric)}</th>
                  <td className="numeric">{formatNumber(metric.used)}</td>
                  <td className="numeric">{formatNumber(metric.limit)}</td>
                  <td className="numeric">{formatNumber(metric.remaining)}</td>
                  <td className="numeric">{formatBillingRatio(metric.ratio)}</td>
                  <td>{billingStatusLabel(metric.status, metric.action)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="hint">Plano sem limites numericos configurados.</p>
        )}
      </section>
    </section>
  );
}
function AiBucketTable(props: { title: string; items: Record<string, AiQualityBucket> }) {
  const entries = sortedBucketEntries(props.items);
  return (
    <section className="panel metric-table metric-detail-table">
      <h2>{props.title}</h2>
      {entries.length ? (
        <table>
          <thead>
            <tr>
              <th>Dimensao</th>
              <th>Total</th>
              <th>Baixa conf.</th>
              <th>Fallback</th>
              <th>Falhas</th>
            </tr>
          </thead>
          <tbody>
            {entries.map(([label, value]) => (
              <tr key={label}>
                <th>{label}</th>
                <td className="numeric">{formatNumber(value.total)}</td>
                <td className="numeric">{formatNumber(value.low_confidence)}</td>
                <td className="numeric">{formatNumber(value.fallback)}</td>
                <td className="numeric">{formatNumber(value.failures)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="hint">Sem avaliacoes de IA no periodo.</p>
      )}
    </section>
  );
}

function AiBreakdownTable(props: { rows: AiQualityBreakdownEntry[] }) {
  return (
    <section className="panel metric-table metric-detail-table metric-table-wide">
      <h2>Provider, modelo, vertical e intencao</h2>
      {props.rows.length ? (
        <table>
          <thead>
            <tr>
              <th>Provider</th>
              <th>Modelo</th>
              <th>Vertical</th>
              <th>Intencao</th>
              <th>Total</th>
              <th>Baixa conf.</th>
              <th>Fallback</th>
              <th>Falhas</th>
              <th>Conf. media</th>
            </tr>
          </thead>
          <tbody>
            {props.rows.slice(0, 20).map((row) => (
              <tr key={`${row.provider}-${row.model}-${row.vertical}-${row.intent}`}>
                <td>{row.provider}</td>
                <td>{row.model}</td>
                <td>{row.vertical}</td>
                <td>{row.intent}</td>
                <td className="numeric">{formatNumber(row.total)}</td>
                <td className="numeric">{formatNumber(row.low_confidence)}</td>
                <td className="numeric">{formatNumber(row.fallback)}</td>
                <td className="numeric">{formatNumber(row.failures)}</td>
                <td className="numeric">{formatOptionalPercent(row.average_confidence)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="hint">Sem linhas para analisar.</p>
      )}
    </section>
  );
}

function AiIncidentTable(props: { rows: AiQualityIncident[] }) {
  return (
    <section className="panel metric-table metric-detail-table metric-table-wide">
      <h2>Eventos recentes</h2>
      {props.rows.length ? (
        <table>
          <thead>
            <tr>
              <th>Data</th>
              <th>Provider</th>
              <th>Modelo</th>
              <th>Intencao</th>
              <th>Conf.</th>
              <th>Motivo</th>
            </tr>
          </thead>
          <tbody>
            {props.rows.slice(0, 12).map((row) => (
              <tr key={row.log_id}>
                <td>{new Date(row.created_at).toLocaleString('pt-BR')}</td>
                <td>{row.provider}</td>
                <td>{row.model}</td>
                <td>{row.intent}</td>
                <td className="numeric">{formatOptionalPercent(row.confidence)}</td>
                <td>{row.failure_reason ?? row.fallback_reason ?? row.policy_reason ?? '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="hint">Sem baixa confianca, fallback ou falha no periodo.</p>
      )}
    </section>
  );
}

export default function MetricsPage() {
  const { tenant_id, hasScope } = useTenantContext();
  const { request } = useApi();
  const [period, setPeriod] = useState<MetricsPeriod>('week');
  const [unitId, setUnitId] = useState('');
  const [metrics, setMetrics] = useState<OperationalMetrics | null>(null);
  const [metering, setMetering] = useState<MeteringUsageResponse | null>(null);
  const [benchmarks, setBenchmarks] = useState<BenchmarkResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!tenant_id || !hasScope('METRICS_VIEW')) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    const range = meteringRange(period);
    const encodedTenant = encodeURIComponent(tenant_id);
    const normalizedUnitId = unitId.trim();
    const unitQuery = normalizedUnitId ? `&unit_id=${encodeURIComponent(normalizedUnitId)}` : '';

    Promise.all([
      request<OperationalMetrics>(`/tenants/${encodedTenant}/metrics?period=${period}${unitQuery}`),
      request<MeteringUsageResponse>(
        `/tenants/${encodedTenant}/metering?from=${range.from}&to=${range.to}`,
      ),
    ])
      .then(([metricsOut, meteringOut]) => {
        if (!cancelled) {
          setMetrics(metricsOut);
          setMetering(meteringOut);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(String(err instanceof Error ? err.message : err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [hasScope, period, request, tenant_id, unitId]);

  useEffect(() => {
    return load();
  }, [load]);

  if (!tenant_id) return <main className="page">Usuario sem tenant configurado.</main>;
  if (!hasScope('METRICS_VIEW')) return <main className="page">Sem permissao para metricas.</main>;

  return (
    <main className="page">
      <header className="page-header">
        <div>
          <h1>Metricas</h1>
          <p>Tenant {tenant_id}</p>
        </div>
        <div className="header-actions">
          <div className="segmented-control" aria-label="Periodo">
            <button
              type="button"
              className={period === 'week' ? 'active' : ''}
              onClick={() => setPeriod('week')}
            >
              7 dias
            </button>
            <button
              type="button"
              className={period === 'month' ? 'active' : ''}
              onClick={() => setPeriod('month')}
            >
              30 dias
            </button>
          </div>
          <label className="compact-filter">
            Unidade
            <input
              value={unitId}
              onChange={(event) => setUnitId(event.target.value)}
              placeholder="unit id"
            />
          </label>
          {hasScope('CONFIG_CHANGE') ? (
            <>
              <Link className="nav-link" to="/config">
                Configuracao
              </Link>
              <Link className="nav-link" to="/simulation">
                Simulacao
              </Link>
            </>
          ) : null}
          <Link className="nav-link" to="/operations">
            Operacao
          </Link>
          <Link className="nav-link" to="/cost-center">
            Custos
          </Link>
          <Link className="nav-link" to="/">
            Inbox
          </Link>
        </div>
      </header>

      {error ? <p className="error">{error}</p> : null}
      {loading && !metrics ? <p className="hint">Carregando metricas...</p> : null}

      {metrics ? (
        <>
          <ExecutiveReportPanel report={metrics.executive_report} />

          <section className="metric-grid">
            <MetricCard label="Abertos" value={formatNumber(metrics.product.open_cases)} />
            <MetricCard
              label="Aguardando humano"
              value={formatNumber(metrics.product.awaiting_human_cases)}
            />
            <MetricCard
              label="Em atendimento"
              value={formatNumber(metrics.product.in_progress_cases)}
            />
            <MetricCard label="Encerrados" value={formatNumber(metrics.product.closed_cases)} />
            <MetricCard
              label="Primeira resposta"
              value={formatDurationSeconds(metrics.product.average_first_response_seconds)}
            />
            <MetricCard
              label="Tempo ate handoff"
              value={formatDurationSeconds(metrics.product.average_time_to_handoff_seconds)}
            />
            <MetricCard label="Handoff" value={formatPercent(metrics.product.handoff_rate)} />
            <MetricCard
              label={`Fora de SLA ${metrics.sla_minutes}m`}
              value={formatNumber(metrics.product.cases_outside_sla)}
              tone={metrics.product.cases_outside_sla > 0 ? 'alert' : undefined}
            />
            <MetricCard
              label="Oportunidades perdidas"
              value={formatNumber(metrics.product.lost_opportunities.total)}
              tone={metrics.product.lost_opportunities.total > 0 ? 'warn' : undefined}
            />
          </section>

          <section className="metric-grid metric-grid-technical">
            <MetricCard
              label="Erros webhook"
              value={formatNumber(metrics.technical.webhook_errors)}
              tone={metrics.technical.webhook_errors > 0 ? 'alert' : undefined}
            />
            <MetricCard
              label="Falhas WhatsApp"
              value={formatNumber(metrics.technical.whatsapp_send_failures)}
              tone={metrics.technical.whatsapp_send_failures > 0 ? 'alert' : undefined}
            />
            <MetricCard
              label="Duplicadas"
              value={formatNumber(metrics.technical.duplicate_messages)}
            />
            <MetricCard
              label="DLQ"
              value={formatNumber(metrics.technical.dlq_events)}
              tone={metrics.technical.dlq_events > 0 ? 'warn' : undefined}
            />
            <MetricCard
              label="Latencia pipeline"
              value={formatMs(metrics.technical.average_pipeline_latency_ms)}
            />
            <MetricCard
              label="Falhas IA"
              value={formatNumber(metrics.technical.ai_failures)}
              tone={metrics.technical.ai_failures > 0 ? 'warn' : undefined}
            />
          </section>

          <BenchmarksPanel benchmarks={benchmarks} />

          <BillingPanel usage={metering} />

          <section className="metric-meta">
            <span>{new Date(metrics.from).toLocaleDateString('pt-BR')}</span>
            <span>{new Date(metrics.to).toLocaleDateString('pt-BR')}</span>
            {metrics.unit_id ? <span>Unidade {metrics.unit_id}</span> : null}
            <span>{new Date(metrics.generated_at).toLocaleString('pt-BR')}</span>
          </section>

          <section className="intent-analytics-section">
            <div className="section-header">
              <div>
                <h2>Analytics por intencao</h2>
                <p className="hint">Conversao, abandono, handoff e perdas no periodo.</p>
              </div>
            </div>
            <IntentAnalyticsTable rows={metrics.product.intent_analytics} />
            <LostOpportunitiesTable metrics={metrics.product.lost_opportunities} />
            <FunnelByIntentTable items={metrics.product.case_funnel_by_intent} />
          </section>

          <section className="ai-quality-section">
            <div className="section-header">
              <div>
                <h2>Qualidade de IA</h2>
                <p className="hint">
                  Relatorio {metrics.technical.ai_quality.report.format} com{' '}
                  {formatNumber(metrics.technical.ai_quality.report.rows)} linhas
                </p>
              </div>
              <button
                type="button"
                className="secondary"
                onClick={() => downloadAiReport(metrics)}
                disabled={!metrics.technical.ai_quality.breakdown.length}
              >
                Exportar CSV
              </button>
            </div>

            <section className="metric-grid metric-grid-technical">
              <MetricCard
                label="Avaliacoes IA"
                value={formatNumber(metrics.technical.ai_quality.total_evaluations)}
              />
              <MetricCard
                label="Baixa confianca"
                value={formatNumber(metrics.technical.ai_quality.low_confidence)}
                tone={metrics.technical.ai_quality.low_confidence > 0 ? 'warn' : undefined}
              />
              <MetricCard
                label="Fallback IA"
                value={formatNumber(metrics.technical.ai_quality.fallback)}
                tone={metrics.technical.ai_quality.fallback > 0 ? 'warn' : undefined}
              />
              <MetricCard
                label="Falhas IA"
                value={formatNumber(metrics.technical.ai_quality.failures)}
                tone={metrics.technical.ai_quality.failures > 0 ? 'alert' : undefined}
              />
              <MetricCard
                label="Conf. media"
                value={formatOptionalPercent(metrics.technical.ai_quality.average_confidence)}
              />
              <MetricCard
                label="Taxa falha"
                value={formatPercent(metrics.technical.ai_quality.failure_rate)}
                tone={metrics.technical.ai_quality.failure_rate > 0 ? 'warn' : undefined}
              />
            </section>

            <div className="metric-columns">
              <AiBucketTable
                title="IA por provider"
                items={metrics.technical.ai_quality.by_provider}
              />
              <AiBucketTable title="IA por modelo" items={metrics.technical.ai_quality.by_model} />
              <AiBucketTable
                title="IA por vertical"
                items={metrics.technical.ai_quality.by_vertical}
              />
              <AiBucketTable
                title="IA por intencao"
                items={metrics.technical.ai_quality.by_intent}
              />
            </div>

            <AiBreakdownTable rows={metrics.technical.ai_quality.breakdown} />
            <AiIncidentTable rows={metrics.technical.ai_quality.recent_events} />
          </section>

          <div className="metric-columns">
            <MetricList title="Volume por intencao" items={metrics.product.volume_by_intent} />
            <MetricList title="Volume por unidade" items={metrics.product.volume_by_unit} />
            <ConversionByIntentTable items={metrics.product.conversion_by_intent} />
            <MetricList title="Status" items={metrics.product.by_status} />
            <MetricList title="Prioridade" items={metrics.product.by_priority} />
            <MetricList title="Filas" items={metrics.product.queues} />
            <MetricList title="Outcome" items={metrics.product.by_outcome} />
            <MetricList
              title="Fallback por provider"
              items={metrics.technical.fallback_by_provider}
            />
          </div>
        </>
      ) : null}
    </main>
  );
}
